import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { employerActions } from 'redux/employer/employerSlice'
import Checkbox from 'core-system/Checkbox'
import Chip from 'core-system/Chip'
import FlexContainer from 'core-system/FlexContainer'
import Loading from 'core-system/Loading'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import { useDispatch, useSelector } from 'react-redux'
import SegmentService from 'redux/config/services/SegmentService'
import { AppState } from 'redux/config/store'
import { MyCommuter } from 'redux/employee/employeeTypes'
import { Segment } from 'redux/employer/employerTypes'
import { Tag } from 'redux/tags/tagsTypes'
import FormattingUtils from 'shared/FormattingUtils'
import RandomUtils from 'shared/RandomUtils'
import styled from 'styled-components'
import CommutersInfoDrawer from './CommutersInfoDrawer'
import { employeeActions } from 'redux/employee/employeeSlice'
import { tagsActions } from 'redux/tags/tagsSlice'
import {
  ORCA_PILOT_GROUP_NAME,
  SSA_COMPANY_ID,
} from 'redux/config/services/Constants'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import CommuterTableOperations from './CommuterTableOperations'
import Avatar from 'core-system/Avatar/Avatar'
import ColorsPill from 'core-system/Pill/ColorPill'
import { qtfActions } from 'redux/qtf/qtfSlice'
import SegmentsDisplay from 'features/Programs/Shared/components/SegmentsDisplay'
import {
  getProgramNamesFromType,
  getVisibleTagNamesSummary,
} from 'features/Segments/helperFunctions'
import { DrawerWidth } from '../components/SegmentsDrawer'

const Body = styled.div`
  margin-top: 1rem;
`

const OperationsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
`

const UserContainer = styled.div`
  display: flex;
`

const NameContainer = styled.div`
  margin-left: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
`

const Programs = styled.div`
  display: flex;
`

const DrawerFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1.25rem ${(props) => props.theme.pxToRem(52)};
  align-items: center;
  background-color: ${(props) => props.theme.palette.white};
  position: fixed;
  bottom: 0;
  right: 0;
  width: ${DrawerWidth}; // Match with the side drawer width
  box-shadow: ${(props) => props.theme.dropShadows.bottom};
  z-index: ${(props) => props.theme.zIndex.max};
  border-top: 1px solid ${(props) => props.theme.palette.grey.grey300};
`

const SegmentsGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
`

const InputORCAStyled = styled.input`
  width: 90%;
`

const RowCell = styled(TableRowCell)`
  padding: 1.5rem 1rem !important;
`

const renderTags = (tagNames: string[] | undefined) => {
  const summary = getVisibleTagNamesSummary(tagNames)

  if (summary.kind === 'empty') {
    return '-'
  }

  if (summary.kind === 'list') {
    return summary.tags.map((tagName) => (
      <Chip variant='filter' isInteractive={false} key={tagName}>
        {tagName}
      </Chip>
    ))
  }

  return (
    <>
      {summary.tags.map((tagName) => (
        <Chip variant='filter' isInteractive={false} key={tagName}>
          {tagName}
        </Chip>
      ))}
      <Text
        variant='action4'
        marginLeft='0.5rem'
        textColor={palette.text.secondary}
      >
        +{summary.overflowCount}
      </Text>
    </>
  )
}

const getSegments = (
  segmentsEnrolled: string[],
  segmentsMap: Dictionary<Segment>
) => {
  const filteredSegments = segmentsEnrolled.filter(
    (segmentId) =>
      segmentsMap[segmentId] &&
      segmentsMap[segmentId].type !== 'ALL' &&
      !segmentsMap[segmentId].archived
  )
  const segmentLen = filteredSegments.length

  if (segmentLen === 0) {
    return '-'
  } else if (segmentLen <= 2) {
    return filteredSegments.map((segmentId) => (
      <Chip variant='filter' isInteractive={false} key={segmentId}>
        {segmentsMap[segmentId].name === 'All Employees'
          ? 'All Commuters'
          : segmentsMap[segmentId].name}
      </Chip>
    ))
  } else {
    return (
      <>
        {filteredSegments.slice(0, 2).map((segmentId) => (
          <Chip variant='filter' isInteractive={false} key={segmentId}>
            {segmentsMap[segmentId].name === 'All Employees'
              ? 'All Commuters'
              : segmentsMap[segmentId].name}
          </Chip>
        ))}
        <Text
          variant='action4'
          marginLeft='0.5rem'
          textColor={palette.text.secondary}
        >
          +{segmentLen - 2}
        </Text>
      </>
    )
  }
}

const getCurrentBalance = (commuter: MyCommuter) => {
  const { remainingProgramBudget, currentIncentives, fleetRemaining } = commuter
  return (
    Number(remainingProgramBudget) +
    Number(currentIncentives) +
    Number(fleetRemaining)
  ) // where null == 0
}

export const getStatusChipAttributes = (commuter: MyCommuter) => {
  const { hasRegistered, hasInvite } = commuter
  if (hasRegistered) {
    return {
      text: 'Registered',
      color: 'green',
    }
  } else if (hasInvite) {
    return {
      text: 'Invited',
      color: 'yellow',
    }
  } else {
    return {
      text: 'Needs Invite',
      color: 'pink',
    }
  }
}

const getTableRows = (
  filteredCommuters: MyCommuter[],
  segmentsMap: Dictionary<Segment>,
  editOrAdd: boolean,
  selectedCommuters: string[],
  handleCommuterSelect: (
    e: React.MouseEvent<HTMLDivElement>,
    id: string,
    selectedCommuters: string[]
  ) => void,
  activeCommuters: Dictionary<MyCommuter>,
  onRowClick: (id: string) => void,
  hasORCA: boolean,
  onChangeORCANumber: (newOrcaNumber: string, commuterId: string) => void,
  userOrcaList: any,
  isAdd: boolean
) => {
  if (!filteredCommuters || filteredCommuters.length === 0) {
    return []
  }
  return filteredCommuters.map((commuter) => {
    const isDisabled = activeCommuters[commuter.id] && true
    const statusChipAttributes = getStatusChipAttributes(commuter)
    const orcaNumber = userOrcaList[commuter.id] || ''
    const handleOnchangeORCAInput = (e) => {
      onChangeORCANumber(e.target.value, commuter.id)
    }

    return (
      <TableRow isClickable={true} key={commuter.id}>
        {editOrAdd && (
          <RowCell>
            <Checkbox
              checked={selectedCommuters.includes(commuter.id)}
              onClick={(e) =>
                !isDisabled &&
                handleCommuterSelect(e, commuter.id, selectedCommuters)
              }
              marginRight='2%'
              disabled={isDisabled}
            />
          </RowCell>
        )}
        <RowCell onClick={() => onRowClick(commuter.id)}>
          <UserContainer>
            <Avatar name={commuter.name} size='medium' />
            <NameContainer>
              <Text variant='action4' hideOverflow>
                {commuter.name}
              </Text>
              <Text variant='body2' textColor={palette.text.placeholder}>
                {commuter.employeeCorporateId
                  ? `#${commuter.employeeCorporateId}`
                  : ''}
              </Text>
            </NameContainer>
          </UserContainer>
        </RowCell>
        <RowCell onClick={() => onRowClick(commuter.id)}>
          <ColorsPill
            color={statusChipAttributes.color as 'gray' | 'yellow' | 'red'}
            text={statusChipAttributes.text}
          />
        </RowCell>
        <RowCell onClick={() => onRowClick(commuter.id)}>
          {commuter.totalTrips} Trip
          {RandomUtils.pluralCheck(commuter.totalTrips)}
        </RowCell>
        <RowCell onClick={() => onRowClick(commuter.id)}>
          <SegmentsGroup>{renderTags(commuter.tagNames)}</SegmentsGroup>
        </RowCell>
        <RowCell onClick={() => onRowClick(commuter.id)}>
          <SegmentsGroup>
            {getSegments(commuter.segmentsEnrolled, segmentsMap)}
          </SegmentsGroup>
        </RowCell>
        <RowCell onClick={() => onRowClick(commuter.id)}>
          <SegmentsDisplay
            segments={commuter.programsEnrolled?.map((programId) => ({
              id: programId,
              text: getProgramNamesFromType(programId.toLowerCase()),
              type: 'program',
            }))}
            displayType='table'
          />
        </RowCell>
        <RowCell onClick={() => onRowClick(commuter.id)}>
          <Programs>
            {FormattingUtils.formatDollar(getCurrentBalance(commuter))}
          </Programs>
        </RowCell>
        <RowCell onClick={() => onRowClick(commuter.id)}>
          {commuter.additionalInfo && commuter.additionalInfo !== 'nan'
            ? commuter.additionalInfo
            : '-'}
        </RowCell>
        {hasORCA && (
          <RowCell onClick={() => onRowClick(commuter.id)}>
            {isAdd ? (
              <InputORCAStyled
                disabled={isDisabled}
                onChange={handleOnchangeORCAInput}
                value={orcaNumber}
                type='number'
              />
            ) : (
              commuter.orcaCardNumber
            )}
          </RowCell>
        )}
      </TableRow>
    )
  })
}

const getActiveCommutersMap = (commuters: MyCommuter[]) => {
  return commuters?.reduce((agg, commuter) => {
    agg[commuter.id] = commuter
    return agg
  }, {})
}

interface CommutersTableProps {
  myCommuters: MyCommuter[]
  activeCommuters: MyCommuter[]
  activeSegment: Segment | null
  activeTag?: Tag
  tagMode?: boolean
  isEdit: boolean
  isAdd: boolean
  setIsEdit?: (isEdit: boolean) => void
  setIsAdd?: (isAdd: boolean) => void
  showCommuterTableOperations?: boolean
  resetPaginationOnRowsChange?: boolean
}

const CommutersTable = React.memo((props: CommutersTableProps) => {
  const {
    myCommuters,
    activeCommuters,
    activeSegment,
    activeTag,
    tagMode = false,
    isEdit,
    isAdd,
    setIsEdit = () => {},
    setIsAdd = () => {},
    showCommuterTableOperations = true,
    resetPaginationOnRowsChange = false,
  } = props

  const dispatch = useDispatch()

  const { segmentUpdateFlag, segmentsMap, profile, allEmployeeSegmentId } =
    useSelector((state: AppState) => state.employer)
  const { tagUpdateFlag } = useSelector((state: AppState) => state.tags)
  const { qtfPrograms, upcomingAllocations } = useSelector(
    (state: AppState) => state.qtf
  )

  const [filteredCommuters, setFilteredCommuters] = useState(myCommuters)
  const [selectedCommuters, setSelectedCommuters] = useState([])
  const [activeEmployeeId, setActiveEmployeeId] = useState(null)
  const [userOrcaList, setUserOrcaList] = useState({})

  useEffect(() => {
    setFilteredCommuters(myCommuters)
    setSelectedCommuters([])
    setUserOrcaList({})
  }, [myCommuters])

  // Pulling upcoming allocations for all the qtf programs for all the commuters
  useEffect(() => {
    try {
      // Getting all employees and not filtering by segment
      dispatch(qtfActions.getQTFUpcomingAllocations({ segmentId: '' }))
    } catch (error) {}
  }, [qtfPrograms])

  const handleCancel = useCallback(() => {
    setSelectedCommuters([])
    setUserOrcaList({})
    if (isAdd) {
      setIsAdd(false)
    } else {
      setIsEdit(false)
    }
    SegmentService.track('commuters-group-manage', {
      action: 'cancel',
      numEmployees: null,
    })
  }, [isAdd, setIsAdd, setIsEdit])

  useEffect(() => {
    if (segmentUpdateFlag) {
      handleCancel()
      dispatch(employeeActions.getMyCommuters({ segment: segmentUpdateFlag }))
      dispatch(employerActions.toggleSegmentUpdateFlag())
    }
  }, [dispatch, handleCancel, segmentUpdateFlag, myCommuters])

  useEffect(() => {
    if (tagUpdateFlag) {
      handleCancel()
      dispatch(
        employeeActions.getMyCommuters({
          segment: allEmployeeSegmentId,
        })
      )
      dispatch(tagsActions.getTags())
      dispatch(tagsActions.toggleTagUpdateFlag(undefined))
    }
  }, [dispatch, handleCancel, tagUpdateFlag, allEmployeeSegmentId])

  //checks which commuters are already part of the program when adding
  const activeCommutersMap = isAdd && getActiveCommutersMap(activeCommuters)
  const editOrAdd = isEdit || isAdd
  const hasORCA =
    !tagMode &&
    activeSegment?.name.toLowerCase() === ORCA_PILOT_GROUP_NAME &&
    profile.id === SSA_COMPANY_ID

  const handleCommuterSelect = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement>,
      id: string,
      selectedCommuters: string[]
    ) => {
      e.stopPropagation()
      if (selectedCommuters.includes(id)) {
        const commuterCopy = [...selectedCommuters]
        const idx = commuterCopy.indexOf(id)
        commuterCopy.splice(idx, 1)
        setSelectedCommuters(commuterCopy)
      } else {
        setSelectedCommuters((prev) => [...prev, id])
      }
    },
    []
  )

  const onRowClick = (id: string) => {
    setActiveEmployeeId(id)
  }

  const onChangeORCANumber = (newOrcaNumber: string, commuterId) => {
    setUserOrcaList((prevList) => ({
      ...prevList,
      [commuterId]: newOrcaNumber,
    }))
  }

  // Memoizing the table rows to prevent unnecessary re-renders
  // Also, when we were clicking the commuter to open the drawer,
  // the table was resetting the pagination
  const tableRows = useMemo(
    () =>
      getTableRows(
        filteredCommuters,
        segmentsMap,
        editOrAdd,
        selectedCommuters,
        handleCommuterSelect,
        activeCommutersMap,
        onRowClick,
        hasORCA,
        onChangeORCANumber,
        userOrcaList,
        isAdd
      ),
    [
      filteredCommuters,
      segmentsMap,
      editOrAdd,
      selectedCommuters,
      activeCommutersMap,
      hasORCA,
      userOrcaList,
      isAdd,
    ]
  )

  const selectAll = useCallback(
    (
      filteredCommuters: MyCommuter[],
      selectedCommuters: string[],
      activeCommuters: Dictionary<MyCommuter>
    ) => {
      const totalValidCommuters = activeCommuters
        ? filteredCommuters.filter((commuter) => !activeCommuters[commuter.id])
        : filteredCommuters

      if (selectedCommuters.length === totalValidCommuters.length) {
        setSelectedCommuters([])
      } else {
        const ids = totalValidCommuters.map((commuters) => commuters.id)
        setSelectedCommuters(ids)
      }
      SegmentService.track('commuters-group-manage', {
        action: 'select-all',
        numEmployees: null,
      })
    },
    []
  )

  const handleAdd = (selectedCommuters: string[]) => {
    if (tagMode && activeTag) {
      dispatch(
        tagsActions.addTagEmployees({
          tagId: activeTag.id,
          employees: selectedCommuters,
        })
      )
      SegmentService.track('commuters-tag-manage', {
        action: 'add',
        numEmployees: selectedCommuters.length,
      })
      return
    }

    if (hasORCA) {
      const selectedCommutersORCAList = selectedCommuters.reduce(
        (commuterORCACards, commuterId) => {
          if (userOrcaList[commuterId]) {
            commuterORCACards[commuterId] = Number(userOrcaList[commuterId])
          }
          return commuterORCACards
        },
        {}
      )
      setUserOrcaList({})

      dispatch(
        employerActions.addSegmentEmployeesWithORCA({
          segmentId: activeSegment.id,
          employees: [...activeSegment.upcomingAdditions, ...selectedCommuters],
          ORCA_card_numbers: selectedCommutersORCAList,
        })
      )
      SegmentService.track('commuters-group-manage', {
        action: 'add',
        numEmployees: [...activeSegment.upcomingAdditions, ...selectedCommuters]
          .length,
      })
    } else {
      dispatch(
        employerActions.addSegmentEmployees({
          segmentId: activeSegment.id,
          employees: [...activeSegment.upcomingAdditions, ...selectedCommuters],
        })
      )
      SegmentService.track('commuters-group-manage', {
        action: 'add',
        numEmployees: [...activeSegment.upcomingAdditions, ...selectedCommuters]
          .length,
      })
    }
  }

  const handleRemove = (selectedCommuters: string[]) => {
    if (tagMode && activeTag) {
      dispatch(
        tagsActions.removeTagEmployees({
          tagId: activeTag.id,
          employees: selectedCommuters,
        })
      )
      SegmentService.track('commuters-tag-manage', {
        action: 'remove',
        numEmployees: selectedCommuters.length,
      })
      return
    }

    dispatch(
      employerActions.removeSegmentEmployees({
        segmentId: activeSegment.id,
        employees: [...activeSegment.upcomingRemovals, ...selectedCommuters],
      })
    )
    SegmentService.track('commuters-group-manage', {
      action: 'remove',
      numEmployees: [...activeSegment.upcomingRemovals, ...selectedCommuters]
        .length,
    })
  }

  if (!myCommuters || !filteredCommuters || (!activeCommuters && isAdd)) {
    return <Loading isCard={false} />
  }

  const HeaderCell = (text: string) => (
    <TableRowCell>
      <Text fontWeight={600} variant={'action4'}>
        {text}
      </Text>
    </TableRowCell>
  )

  const CheckboxHeaderCell = () => (
    <TableRowCell>
      <Checkbox
        checked={
          activeCommuters &&
          filteredCommuters.length ===
            selectedCommuters.length + activeCommuters.length
        }
        onClick={() =>
          selectAll(filteredCommuters, selectedCommuters, activeCommutersMap)
        }
        marginRight='2%'
        disabled={filteredCommuters.length === 0}
        data-cy='select-all-commuters-checkbox'
      />
    </TableRowCell>
  )

  const headerCells = [
    editOrAdd && <CheckboxHeaderCell />,
    HeaderCell('Commuter'),
    HeaderCell('Status'),
    HeaderCell('Total Trips'),
    HeaderCell('Tags'),
    HeaderCell('Groups'),
    HeaderCell('Programs'),
    HeaderCell('Current Balance'),
    HeaderCell('Additional Info'),
    hasORCA && HeaderCell('ORCA Card Number'),
  ]

  return (
    <>
      {showCommuterTableOperations && (
        <OperationsContainer>
          <CommuterTableOperations
            commuters={myCommuters}
            filteredCommuters={filteredCommuters}
            setFilteredCommuters={setFilteredCommuters}
          />
        </OperationsContainer>
      )}
      <Body>
        <Table
          maxRows={10}
          headerCells={headerCells}
          rows={tableRows}
          stickyColumns={1}
          resetPaginationOnRowsChange={resetPaginationOnRowsChange}
          tablePaginationProps={{ count: 1736 }}
        />
      </Body>
      {editOrAdd && (
        <DrawerFooter>
          <Text variant='action1'>{selectedCommuters.length} Selected</Text>
          <FlexContainer>
            <SecondaryButton
              style={{ marginRight: '1rem' }}
              onClick={handleCancel}
            >
              Cancel
            </SecondaryButton>
            {isAdd && (
              <PrimaryButton
                disabled={selectedCommuters.length === 0}
                onClick={() => handleAdd(selectedCommuters)}
              >
                {tagMode ? 'Add To Tag' : 'Add To Group'}
              </PrimaryButton>
            )}
            {isEdit && (
              <PrimaryButton
                disabled={selectedCommuters.length === 0}
                onClick={() => handleRemove(selectedCommuters)}
              >
                {tagMode ? 'Remove From Tag' : 'Remove From Group'}
              </PrimaryButton>
            )}
          </FlexContainer>
        </DrawerFooter>
      )}
      <CommutersInfoDrawer
        employeeId={activeEmployeeId}
        open={activeEmployeeId && true}
        closeModal={() => setActiveEmployeeId(null)}
        upcomingAllocations={upcomingAllocations}
      />
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  CommutersTable.displayName = 'CommutersTable'
}

export default CommutersTable
